/**
 * ExpertCapacityHeatMap - Visual Expert Capacity Grid
 *
 * FERP CRITICAL: Visual capacity monitoring for life-critical expert routing
 *
 * Features:
 * - Color-coded capacity grid (green <50%, yellow 50-80%, red >80%)
 * - Tier grouping (Tier 1, Tier 2, Tier 3)
 * - Interactive expert selection
 * - Real-time capacity updates
 * - Specialization badges
 * - Compliance certification indicators
 *
 * Architecture Principles:
 * - Data-driven (all expert data from backend)
 * - Backend ID generation (Snowflake IDs)
 * - ActorDescriptor primitive (Expert = Actor with metadata)
 * - Life-critical visual indicators
 */

import React from 'react';
import { Grid, Card, Badge, Group, Text, Progress, Tooltip, Title, Alert } from '@mantine/core';
import { IconUser, IconAlertCircle } from '@tabler/icons-react';
import { ActorDescriptor } from '../types-16-complete';
import { ExpertMetadata } from '../services/ExpertRoutingService';
import { TeamCapacityMetrics, ExpertsByTier } from './hooks/useExpertRouting';

export interface ExpertCapacityHeatMapProps {
  expertsByTier: ExpertsByTier;
  teamCapacityMetrics: TeamCapacityMetrics | null;
  selectedExpertId: string | null;
  onExpertSelect: (expertId: string) => void;
  isLoading: boolean;
}

/**
 * Expert Capacity Heat Map - Visual Grid Component
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (expert data from ActorRepository)
 * - ✅ Backend ID generation (Snowflake IDs)
 * - ✅ ActorDescriptor primitive (16 primitives)
 * - ✅ Life-critical visual indicators
 * - ✅ Real-time capacity updates (polling)
 */
export function ExpertCapacityHeatMap({
  expertsByTier,
  teamCapacityMetrics,
  selectedExpertId,
  onExpertSelect,
  isLoading,
}: ExpertCapacityHeatMapProps) {
  /**
   * Get capacity color for progress bar
   * FERP: Visual thresholds for capacity monitoring
   */
  const getCapacityColor = (capacity: number): string => {
    if (capacity < 50) return 'green';
    if (capacity < 80) return 'yellow';
    return 'red';
  };

  /**
   * Get availability status color for badge
   * FERP: Visual indicators for expert availability
   */
  const getAvailabilityColor = (status: string): string => {
    switch (status) {
      case 'available':
        return 'green';
      case 'busy':
        return 'yellow';
      case 'offline':
        return 'gray';
      default:
        return 'gray';
    }
  };

  /**
   * Render expert card
   * FERP: Visual card for expert with capacity, specializations, compliance
   */
  const renderExpertCard = (expert: ActorDescriptor, tier: string) => {
    const metadata = expert.metadata as ExpertMetadata;
    const isSelected = selectedExpertId === expert.id;

    return (
      <Card
        key={expert.id}
        p="sm"
        withBorder
        style={{
          cursor: 'pointer',
          borderColor: isSelected ? '#228be6' : undefined,
          borderWidth: isSelected ? 2 : 1,
          backgroundColor: isSelected ? '#e7f5ff' : undefined,
        }}
        onClick={() => onExpertSelect(expert.id)}
      >
        {/* Expert Name and Status */}
        <Group justify="space-between" mb="xs">
          <Group gap="xs">
            <IconUser size={16} />
            <Text size="sm" fw={500}>
              {expert.name}
            </Text>
          </Group>
          <Badge
            color={getAvailabilityColor(metadata.availabilityStatus)}
            size="xs"
          >
            {metadata.availabilityStatus}
          </Badge>
        </Group>

        {/* Capacity Progress Bar */}
        <Tooltip
          label={`Current capacity: ${metadata.currentCapacity}%`}
          position="top"
        >
          <Progress
            value={metadata.currentCapacity}
            color={getCapacityColor(metadata.currentCapacity)}
            size="lg"
            mb="xs"
          />
        </Tooltip>

        {/* Expert Metrics */}
        <Group gap="xs" mb="xs">
          <Text size="xs" color="dimmed">
            {metadata.currentCapacity}% capacity
          </Text>
          <Text size="xs" color="dimmed">
            •
          </Text>
          <Text size="xs" color="dimmed">
            ${metadata.hourlyRate}/hr
          </Text>
          <Text size="xs" color="dimmed">
            •
          </Text>
          <Text size="xs" color="dimmed">
            {metadata.successRate}% success
          </Text>
        </Group>

        {/* Specializations */}
        {metadata.specializations && metadata.specializations.length > 0 && (
          <Group gap="xs" mb="xs">
            {metadata.specializations.slice(0, 3).map((spec) => (
              <Badge key={spec} size="xs" variant="outline">
                {spec}
              </Badge>
            ))}
            {metadata.specializations.length > 3 && (
              <Badge size="xs" variant="outline">
                +{metadata.specializations.length - 3}
              </Badge>
            )}
          </Group>
        )}

        {/* Compliance Certifications */}
        {metadata.compliance && (
          <Group gap="xs" mt="xs">
            {metadata.compliance.hipaaValidated && (
              <Badge size="xs" color="blue">
                HIPAA
              </Badge>
            )}
            {metadata.compliance.soxValidated && (
              <Badge size="xs" color="purple">
                SOX
              </Badge>
            )}
            {metadata.compliance.gdprValidated && (
              <Badge size="xs" color="teal">
                GDPR
              </Badge>
            )}
          </Group>
        )}

        {/* Response Time */}
        {metadata.averageResponseTime && (
          <Text size="xs" color="dimmed" mt="xs">
            Avg response: {metadata.averageResponseTime}h
          </Text>
        )}
      </Card>
    );
  };

  return (
    <div>
      {/* Team Capacity Alert */}
      {teamCapacityMetrics?.shouldHandoffToSpecialists && (
        <Alert
          color="orange"
          icon={<IconAlertCircle size={16} />}
          mb="md"
        >
          <Text size="sm" fw={500}>
            Team At Capacity ({teamCapacityMetrics.avgCapacity.toFixed(0)}%)
          </Text>
          <Text size="xs" mt="xs">
            Consider routing new projects to Tier 2 specialists to maintain service quality
            and prevent overload on internal team.
          </Text>
        </Alert>
      )}

      {/* Tier 1: In-House FERP Core Experts */}
      <div style={{ marginBottom: '2rem' }}>
        <Group justify="space-between" mb="sm">
          <div>
            <Title order={4}>Tier 1: FERP Core Experts</Title>
            <Text size="sm" color="dimmed">
              $120-150/hr • In-house experts • Standard projects
            </Text>
          </div>
          <Badge color="blue" size="lg">
            {expertsByTier.tier1.length} experts
          </Badge>
        </Group>

        {expertsByTier.tier1.length === 0 ? (
          <Alert color="gray" icon={<IconAlertCircle size={16} />}>
            No Tier 1 experts found. Experts are loaded from backend via ActorRepository.
          </Alert>
        ) : (
          <Grid>
            {expertsByTier.tier1.map((expert) => (
              <Grid.Col key={expert.id} span={4}>
                {renderExpertCard(expert, 'tier1')}
              </Grid.Col>
            ))}
          </Grid>
        )}
      </div>

      {/* Tier 2: Certified Partner Experts */}
      <div style={{ marginBottom: '2rem' }}>
        <Group justify="space-between" mb="sm">
          <div>
            <Title order={4}>Tier 2: Certified Partner Experts</Title>
            <Text size="sm" color="dimmed">
              $200+/hr • External specialists • Complex projects
            </Text>
          </div>
          <Badge color="purple" size="lg">
            {expertsByTier.tier2.length} experts
          </Badge>
        </Group>

        {expertsByTier.tier2.length === 0 ? (
          <Alert color="gray" icon={<IconAlertCircle size={16} />}>
            No Tier 2 experts found. Experts are loaded from backend via ActorRepository.
          </Alert>
        ) : (
          <Grid>
            {expertsByTier.tier2.map((expert) => (
              <Grid.Col key={expert.id} span={4}>
                {renderExpertCard(expert, 'tier2')}
              </Grid.Col>
            ))}
          </Grid>
        )}
      </div>

      {/* Tier 3: AI-Enhanced Self-Service */}
      <div>
        <Group justify="space-between" mb="sm">
          <div>
            <Title order={4}>Tier 3: AI-Enhanced Self-Service</Title>
            <Text size="sm" color="dimmed">
              $25-50/hr • Automated workflows • Simple integrations
            </Text>
          </div>
          <Badge color="teal" size="lg">
            {expertsByTier.tier3.length} experts
          </Badge>
        </Group>

        {expertsByTier.tier3.length === 0 ? (
          <Alert color="gray" icon={<IconAlertCircle size={16} />}>
            No Tier 3 experts found. Experts are loaded from backend via ActorRepository.
          </Alert>
        ) : (
          <Grid>
            {expertsByTier.tier3.map((expert) => (
              <Grid.Col key={expert.id} span={4}>
                {renderExpertCard(expert, 'tier3')}
              </Grid.Col>
            ))}
          </Grid>
        )}
      </div>

      {/* Legend */}
      <Card mt="xl" p="md" withBorder style={{ backgroundColor: '#f8f9fa' }}>
        <Title order={5} mb="sm">
          Capacity Legend
        </Title>
        <Group gap="xl">
          <Group gap="xs">
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#51cf66',
                borderRadius: 4,
              }}
            />
            <Text size="xs">Available (&lt;50%)</Text>
          </Group>
          <Group gap="xs">
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#ffd43b',
                borderRadius: 4,
              }}
            />
            <Text size="xs">Busy (50-80%)</Text>
          </Group>
          <Group gap="xs">
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#ff6b6b',
                borderRadius: 4,
              }}
            />
            <Text size="xs">At Capacity (&gt;80%)</Text>
          </Group>
        </Group>
      </Card>
    </div>
  );
}
